import { useEffect } from 'react'
import { siteSettingsSchema } from '@/features/website-builder/schema/site'
import { restaurantScopedStorage } from '@/shared/store/restaurantScopedStorage'
import { RESTAURANT_SETTINGS_USE_API } from '../config'
import {
  addressFromSiteSettings,
  contactFromSiteSettings,
  hoursFromSiteSettings,
  identityFromSiteSettings
} from '../adapters/fromSiteSettings'
import { useRestaurantSettingsStore } from '../store/useRestaurantSettingsStore'

const WEBSITE_STORAGE_KEY = 'website-builder-storage'

/**
 * Reads the venue facts out of the website builder's persisted draft, if there
 * are any.
 *
 * Deliberately going through localStorage rather than importing the website store:
 * importing it would instantiate the builder's whole document machinery — starter
 * template, history stacks, migrations — on a page that has nothing to do with it,
 * and would make this panel fail if the builder's persisted document were
 * unparseable. Anything that does not parse as venue settings is simply skipped.
 */
function readWebsiteSettings() {
  try {
    // Through the scoped storage the builder itself uses, so a venue is only
    // ever seeded from its own website, never from another venue's.
    const raw = restaurantScopedStorage.getItem(WEBSITE_STORAGE_KEY) as
      | string
      | null
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { draft?: { venue?: unknown } } }
    const site = parsed.state?.draft?.venue
    if (!site) return null
    const result = siteSettingsSchema.safeParse(site)
    return result.success ? result.data : null
  } catch {
    return null
  }
}

/**
 * Brings the settings store up to date, from whichever source there is.
 *
 * One function and three possible origins, in priority order:
 *
 * 1. the endpoint, once `RESTAURANT_SETTINGS_USE_API` is on;
 * 2. the external onboarding form, which calls `seedAddress` directly — it does
 *    not exist in this repo yet, which is why "no address at all" is a
 *    first-class state in the panel rather than an error;
 * 3. a website document this browser already had, so a venue that typed its
 *    address into the builder before this page existed does not lose it.
 *
 * It also guards the restaurant identity: `restaurantId` lives in localStorage
 * and changes when an owner of two venues signs in to the other one, and the
 * persisted settings must not follow them across.
 */
export function useRestaurantSettingsHydration() {
  const seededFrom = useRestaurantSettingsStore(s => s.seededFrom)
  const storedRestaurantId = useRestaurantSettingsStore(s => s.restaurantId)

  useEffect(() => {
    const restaurantId = localStorage.getItem('restaurantId')
    const store = useRestaurantSettingsStore.getState()

    if (storedRestaurantId && storedRestaurantId !== restaurantId) {
      store.reset(restaurantId)
      return
    }
    // Data that was never stamped (new, or saved before the stamp existed) is
    // claimed by the venue open now, which is the one its storage key names.
    // Unstamped, the check above could never fire.
    if (!storedRestaurantId && restaurantId) store.claim(restaurantId)

    if (RESTAURANT_SETTINGS_USE_API) {
      /*
        The GET goes here, mapped through a mapper beside the API file exactly as
        features/floor-plan/api does it, then `importSettings(document)`.
        Intentionally absent rather than stubbed: a fake request that resolves is
        worse than none, because it makes the page look wired when it is not.
      */
      return
    }

    if (seededFrom !== 'none') return

    const site = readWebsiteSettings()
    if (!site) return

    // First, and through the seed rather than a save: the seed is what marks
    // this rescue done, and the saves below leave a source other than `none`
    // alone. A save is the owner's, so once one has happened this never runs.
    store.seedAddress(addressFromSiteSettings(site), 'website-builder')

    const identity = identityFromSiteSettings(site)
    store.saveIdentity({ ...store.settings.identity, ...identity })
    store.saveContact({ ...store.settings.contact, ...contactFromSiteSettings(site) })

    const hours = hoursFromSiteSettings(site)
    if (hours) store.saveHours(hours)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isBackedByApi: RESTAURANT_SETTINGS_USE_API }
}
